import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { PDFFile } from '@adornis/pdf/db/pdf-file.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { OWL_PNG_BASE64 } from './pdf-logo-data.js';
import { Exam } from '../exam.js';

// ─── Shared PDF utilities (mirrored from generate-grading-reports.ts) ────────

function unwrapDoc<T>(value: T | { doc?: T } | null | undefined): T | null {
  if (!value) return null;
  if (typeof value === 'object' && 'doc' in (value as any)) return ((value as any).doc as T) ?? null;
  return value as T;
}

function preserveNewlines(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(/[ \t]+/g, ' ').trim();
}

function toPdfSafeText(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[⁰⁴⁵⁶⁷⁸⁹]/g, m =>
      ({ '⁰': '0', '⁴': '4', '⁵': '5', '⁶': '6', '⁷': '7', '⁸': '8', '⁹': '9' }[m] ?? ''),
    )
    .replace(/[²]/g, '2').replace(/[³]/g, '3').replace(/[¹]/g, '1')
    .replace(/[–—]/g, '-').replace(/[‘’]/g, "'").replace(/[“”]/g, '"')
    .replace(/[…]/g, '...').replace(/[^\x09\x0A\x0D\x20-\xFF]/g, '');
}

function cleanInline(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1').replace(/_([^_]+)_/g, '$1')
    .replace(/`([^`]+)`/g, '$1').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]+\)/g, '[Bild]');
}

function latexToSafeText(text: string): string {
  if (!text) return text;
  return text
    .replace(/\\begin\{[^}]+\}/g, '').replace(/\\end\{[^}]+\}/g, '')
    .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '($1)/($2)')
    .replace(/\\sqrt\{([^}]+)\}/g, 'sqrt($1)').replace(/\\sqrt/g, 'sqrt')
    .replace(/\^\{([^}]+)\}/g, '^($1)').replace(/\^2/g, '²').replace(/\^3/g, '³')
    .replace(/\_\{([^}]+)\}/g, '_($1)')
    .replace(/\\alpha/g, 'alpha').replace(/\\beta/g, 'beta').replace(/\\gamma/g, 'gamma')
    .replace(/\\delta/g, 'delta').replace(/\\Delta/g, 'Delta').replace(/\\pi/g, 'pi')
    .replace(/\\times/g, '×').replace(/\\div/g, '÷').replace(/\\pm/g, '±')
    .replace(/\\cdot/g, '·').replace(/\\leq/g, '<=').replace(/\\geq/g, '>=')
    .replace(/\\neq/g, '!=').replace(/\\infty/g, 'inf').replace(/\\to\b/g, '->')
    .replace(/\\rightarrow/g, '->').replace(/\\Rightarrow/g, '=>')
    .replace(/\\[a-zA-Z]+\{([^}]*)\}/g, '$1').replace(/\\[a-zA-Z]+/g, '')
    .replace(/\$\$/g, '').replace(/\$/g, '').replace(/\{|\}/g, '');
}

type LineStyle = 'h1' | 'h2' | 'h3' | 'body' | 'code' | 'bullet' | 'blockquote';
interface RichLine { text: string; style: LineStyle }

function parseMarkdownLines(text: string): RichLine[] {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const result: RichLine[] = [];
  let inCodeBlock = false;
  for (const line of lines) {
    const t = line.trimEnd();
    if (t.startsWith('```')) { inCodeBlock = !inCodeBlock; continue; }
    if (inCodeBlock)              { result.push({ text: t, style: 'code' }); continue; }
    if (t.startsWith('### '))     { result.push({ text: cleanInline(t.slice(4)), style: 'h3' }); }
    else if (t.startsWith('## ')) { result.push({ text: cleanInline(t.slice(3)), style: 'h2' }); }
    else if (t.startsWith('# '))  { result.push({ text: cleanInline(t.slice(2)), style: 'h1' }); }
    else if (/^[-*+]\s/.test(t))  { result.push({ text: cleanInline(t.replace(/^[-*+]\s+/, '')), style: 'bullet' }); }
    else if (/^\d+\.\s/.test(t))  { result.push({ text: cleanInline(t), style: 'bullet' }); }
    else if (t.startsWith('> '))  { result.push({ text: cleanInline(t.slice(2)), style: 'blockquote' }); }
    else                          { result.push({ text: cleanInline(t), style: 'body' }); }
  }
  return result;
}

function wrapText(text: string, font: any, fontSize: number, maxWidth: number): string[] {
  const normalized = toPdfSafeText(text).replace(/\r\n/g, '\n');
  const paragraphs = normalized.split('\n');
  const lines: string[] = [];
  for (const paragraph of paragraphs) {
    const trimmed = paragraph.trim();
    if (!trimmed) { lines.push(''); continue; }
    const words = trimmed.split(/\s+/);
    let currentLine = '';
    for (const word of words) {
      const candidate = currentLine ? `${currentLine} ${word}` : word;
      if (font.widthOfTextAtSize(candidate, fontSize) <= maxWidth || !currentLine) {
        currentLine = candidate;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }
    if (currentLine) lines.push(currentLine);
  }
  return lines.length > 0 ? lines : [''];
}

// ─── MS/GS task extraction ────────────────────────────────────────────────────

interface MsGsTaskRow {
  label: string;
  masterSolution: string;
  gradingCriteria: string;
  maxPoints: number;
}

export function walkExamTasks(tasks: any[], path: string[] = []): MsGsTaskRow[] {
  const rows: MsGsTaskRow[] = [];
  for (const task of tasks) {
    if (!task) continue;
    const taskNum = String(task.taskNumber ?? task.name ?? (path.length + 1));
    const currentPath = path.length === 0 ? [taskNum] : [...path, taskNum.replace(/^\d+\./, '')];

    const subTasks: any[] = Array.isArray(task.subTasks) ? task.subTasks : [];
    if (subTasks.length > 0) {
      rows.push(...walkExamTasks(subTasks, currentPath));
    } else if (task.needsAnswer !== false) {
      const label =
        currentPath.length === 1
          ? `Aufgabe ${currentPath[0]}`
          : `Aufgabe ${currentPath[0]} - Teilaufgabe ${currentPath.slice(1).join(' > ')}`;

      const ms = unwrapDoc<any>(task.masterSolution);
      const gs = unwrapDoc<any>(task.gradingScheme);

      rows.push({
        label,
        masterSolution: preserveNewlines(ms?.content ?? (typeof task.masterSolution === 'string' ? task.masterSolution : '')),
        gradingCriteria: preserveNewlines(gs?.criteria ?? (typeof task.gradingScheme === 'string' ? task.gradingScheme : '')),
        maxPoints: Number(gs?.maxPoints ?? task.maxPoints ?? 0) || 0,
      });
    }
  }
  return rows;
}

// ─── PDF generation ───────────────────────────────────────────────────────────

export async function buildMsGsPDFBytes(
  exam: any,
  type: 'masterSolution' | 'gradingScheme' | 'both',
): Promise<Uint8Array> {
  const tasks = Array.isArray(exam?.tasks) ? exam.tasks : [];
  const rows = walkExamTasks(tasks);
  const wantsMs = type === 'masterSolution' || type === 'both';
  const wantsGs = type === 'gradingScheme' || type === 'both';

  const doc = await PDFDocument.create();
  const PW = 595, PH = 842, M = 40, CW = PW - M * 2;
  const fontR    = await doc.embedFont(StandardFonts.Helvetica);
  const fontB    = await doc.embedFont(StandardFonts.HelveticaBold);
  const fontMono = await doc.embedFont(StandardFonts.Courier);
  const logoImg  = await doc.embedPng(OWL_PNG_BASE64);

  const brand    = rgb(0.333, 0.580, 0.812);
  const brandMid = rgb(0.600, 0.749, 0.886);
  const brandBg  = rgb(0.941, 0.969, 1.000);
  const white    = rgb(1, 1, 1);
  const textDark = rgb(0.059, 0.090, 0.165);
  const textGray = rgb(0.396, 0.455, 0.549);
  const rowAlt   = rgb(0.953, 0.961, 0.970);
  const border   = rgb(0.882, 0.906, 0.929);
  const violet   = rgb(0.549, 0.314, 0.859);
  const violetBg = rgb(0.972, 0.953, 1.000);

  let page = doc.addPage([PW, PH]);
  let y = PH;

  const nextPage = () => { page = doc.addPage([PW, PH]); y = PH - M; };
  const need = (h: number) => { if (y - h < M + 30) nextPage(); };

  const drawRichContent = (content: string, x: number, maxW: number, baseSize = 9) => {
    if (!content) return;
    const richLines = parseMarkdownLines(latexToSafeText(content));
    for (const rl of richLines) {
      const isHead = rl.style === 'h1' || rl.style === 'h2' || rl.style === 'h3';
      const font   = rl.style === 'code' ? fontMono : (isHead ? fontB : fontR);
      const fSize  = rl.style === 'h1' ? baseSize + 3 : rl.style === 'h2' ? baseSize + 2 : rl.style === 'h3' ? baseSize + 1 : baseSize;
      const indent = rl.style === 'bullet' ? 10 : rl.style === 'blockquote' ? 14 : 0;
      const prefix = rl.style === 'bullet' ? '- ' : '';
      const color  = isHead ? brand : (rl.style === 'blockquote' ? textGray : textDark);
      const raw    = toPdfSafeText(prefix + rl.text);
      if (!raw && rl.style === 'body') { y -= 4; continue; }
      const wrapped = raw ? wrapText(raw, font, fSize, maxW - indent) : [''];
      const blockH  = wrapped.length * (fSize + 3) + (rl.style === 'code' ? 4 : 0);
      need(blockH + 2);
      if (rl.style === 'code') {
        page.drawRectangle({ x: x + indent - 2, y: y - blockH + 2, width: maxW - indent + 2, height: blockH - 2, color: rowAlt });
      }
      if (rl.style === 'blockquote') {
        page.drawRectangle({ x: x + indent - 4, y: y - blockH + 2, width: 2, height: blockH - 2, color: brandMid });
      }
      for (const wl of wrapped) {
        need(fSize + 3);
        if (wl) page.drawText(wl, { x: x + indent, y, size: fSize, font, color });
        y -= fSize + 3;
      }
      if (isHead) y -= 2;
    }
  };

  // ── Header band ──────────────────────────────────────────────────────────────
  const examTitle = (exam?.title ?? '').replace(/\s+/g, ' ').trim() || 'Klausur';
  const docTitle =
    type === 'masterSolution'
      ? 'Musterloesung'
      : type === 'gradingScheme'
        ? 'Bewertungsschema'
        : 'Musterloesung & Bewertungsschema';
  const docTitleDisplay =
    type === 'masterSolution'
      ? 'Musterlösung'
      : type === 'gradingScheme'
        ? 'Bewertungsschema'
        : 'Musterlösung & Bewertungsschema';

  const HH = 70;
  page.drawRectangle({ x: 0, y: PH - HH, width: PW, height: HH, color: brand });
  page.drawRectangle({ x: M, y: PH - HH + 13, width: 44, height: 44, color: white });
  page.drawImage(logoImg, { x: M + 2, y: PH - HH + 15, width: 40, height: 40 });
  page.drawText('gradias', { x: M + 52, y: PH - HH + 35, size: 16, font: fontB, color: white });
  page.drawLine({ start: { x: M + 132, y: PH - 12 }, end: { x: M + 132, y: PH - HH + 10 }, thickness: 0.5, color: white });
  page.drawText(toPdfSafeText(docTitleDisplay), { x: M + 142, y: PH - 26, size: 14, font: fontB, color: white });
  const subtitle = examTitle.length > 60 ? `${examTitle.slice(0, 57)}...` : examTitle;
  page.drawText(toPdfSafeText(subtitle), { x: M + 142, y: PH - 45, size: 9, font: fontR, color: brandMid });
  y = PH - HH;

  // Info row
  page.drawRectangle({ x: 0, y: y - 24, width: PW, height: 24, color: rowAlt });
  page.drawText(
    toPdfSafeText(`Generiert am: ${new Date().toLocaleString('de-DE')}`),
    { x: M, y: y - 16, size: 8.5, font: fontR, color: textGray },
  );
  y -= 36;

  page.drawLine({ start: { x: M, y }, end: { x: PW - M, y }, thickness: 0.5, color: border });
  y -= 14;

  // ── Task rows ─────────────────────────────────────────────────────────────────
  for (const row of rows) {
    const hasMs = wantsMs && row.masterSolution.trim().length > 0;
    const hasGs = wantsGs && row.gradingCriteria.trim().length > 0;
    if (!hasMs && !hasGs) continue;

    need(60);

    // Task header bar
    page.drawRectangle({ x: M, y: y - 22, width: CW, height: 22, color: brandBg });
    page.drawRectangle({ x: M, y: y - 22, width: 4, height: 22, color: brand });
    page.drawText(toPdfSafeText(row.label), { x: M + 10, y: y - 15, size: 10.5, font: fontB, color: textDark });
    y -= 28;

    // Musterlösung section
    if (hasMs) {
      need(30);
      page.drawText('Musterloesung:', { x: M + 8, y, size: 8.5, font: fontB, color: brand });
      y -= 13;
      drawRichContent(row.masterSolution, M + 16, CW - 32);
      y -= 4;
    }

    // Bewertungsschema section
    if (hasGs) {
      need(30);
      // violet accent for GS
      page.drawText('Bewertungsschema:', { x: M + 8, y, size: 8.5, font: fontB, color: violet });
      y -= 13;
      // Max points badge
      const mpTxt = toPdfSafeText(`Max. Punkte: ${row.maxPoints}`);
      const mpW = fontB.widthOfTextAtSize(mpTxt, 8);
      page.drawRectangle({ x: M + 16, y: y - 14, width: mpW + 8, height: 14, color: violetBg });
      page.drawText(mpTxt, { x: M + 20, y: y - 10, size: 8, font: fontB, color: violet });
      y -= 18;
      drawRichContent(row.gradingCriteria, M + 16, CW - 32);
      y -= 4;
    }

    y -= 2;
    page.drawLine({ start: { x: M, y }, end: { x: PW - M, y }, thickness: 0.3, color: border });
    y -= 12;
  }

  // ── Footers ───────────────────────────────────────────────────────────────────
  const pageCount = doc.getPageCount();
  doc.getPages().forEach((p, i) => {
    const ft = `Gradias  |  ${toPdfSafeText(docTitle)}  |  Seite ${i + 1} von ${pageCount}`;
    const ftW = fontR.widthOfTextAtSize(ft, 8);
    p.drawLine({ start: { x: M, y: 28 }, end: { x: PW - M, y: 28 }, thickness: 0.3, color: border });
    p.drawText(ft, { x: (PW - ftW) / 2, y: 16, size: 8, font: fontR, color: textGray });
  });

  return doc.save();
}

// ─── Mutation ─────────────────────────────────────────────────────────────────

export const generateMsGsFileMutation = registerMutation({
  operationName: 'generateMsGsFile',
  type: () => String,
  params: [
    { name: 'examId', type: () => String },
    { name: 'type', type: () => String },
  ],
  resolve: (examId: string, type: string) => async () => {
    const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
    const currentUserId = currentUser?._id;
    if (!currentUserId) throw new Error('Nicht eingeloggt.');

    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId });
    if (!exam) throw new Error('Klausur wurde nicht gefunden.');
    if (exam.userID && exam.userID !== currentUserId) throw new Error('Keine Berechtigung für diese Klausur.');

    const downloadType = (['masterSolution', 'gradingScheme', 'both'] as const).includes(
      type as 'masterSolution' | 'gradingScheme' | 'both',
    )
      ? (type as 'masterSolution' | 'gradingScheme' | 'both')
      : 'both';

    const pdfBytes = await buildMsGsPDFBytes(exam, downloadType);
    const dateStr = new Date().toISOString().slice(0, 10);
    const suffix =
      downloadType === 'masterSolution'
        ? 'Musterloesung'
        : downloadType === 'gradingScheme'
          ? 'Bewertungsschema'
          : 'Musterloesung-Bewertungsschema';
    const filename = `${suffix}_${dateStr}.pdf`;

    const file = await PDFFile.createFromBuffer(Buffer.from(pdfBytes), filename, {
      userID: String(currentUserId),
      examId: String(examId),
      fileType: 'ms-gs-export',
    });

    if (!file._id) throw new Error('PDF konnte nicht gespeichert werden.');
    return file._id;
  },
});
